import { useEffect, useState } from 'react'
import { apiClient } from '@/services/apiClient'

export interface MediaBlobState {
  objectUrl: string | null
  /** The fetch answered 404: the file is gone. Never retried. */
  gone: boolean
  /** Any other failure, after one retry. */
  error: boolean
}

const LOADING: MediaBlobState = { objectUrl: null, gone: false, error: false }

/** A hold on one file's bytes: `result` settles once, `release` lets go (a
 * second call does nothing). */
export interface MediaBlobLease {
  result: Promise<MediaBlobState>
  release: () => void
}

interface Entry {
  result: Promise<MediaBlobState>
  holders: number
  objectUrl: string | null
  released: boolean
}

const entries = new Map<string, Entry>()

/**
 * The media route is Bearer-auth-gated, so a bare <img src> would 401: fetch
 * through apiClient (the JWT header) and render from an object URL. One retry
 * on a transient failure; a 404 is final.
 *
 * SA-1122: one fetch per file for everyone holding it. The email frame and the
 * attachment row (or grid cell) that show one image share its blob, keyed by
 * the media URL. The object URL is revoked when the last holder lets go, and
 * a failure is shared only while someone holds it, so a later mount tries
 * again.
 */
export function acquireMediaBlob(mediaUrl: string): MediaBlobLease {
  let entry = entries.get(mediaUrl)
  if (!entry) {
    const made: Entry = { result: Promise.resolve(LOADING), holders: 0, objectUrl: null, released: false }
    made.result = load(mediaUrl, made)
    entries.set(mediaUrl, made)
    entry = made
  }
  const held = entry
  held.holders += 1
  let active = true
  return {
    result: held.result,
    release: () => {
      if (!active) return
      active = false
      held.holders -= 1
      if (held.holders > 0) return
      held.released = true
      if (entries.get(mediaUrl) === held) entries.delete(mediaUrl)
      if (held.objectUrl) URL.revokeObjectURL(held.objectUrl)
    },
  }
}

async function load(mediaUrl: string, entry: Entry): Promise<MediaBlobState> {
  try {
    let res = await apiClient.fetch(mediaUrl)
    if (res.status !== 404 && !res.ok) res = await apiClient.fetch(mediaUrl)
    if (res.status === 404) return { objectUrl: null, gone: true, error: false }
    if (!res.ok) throw new Error(`status ${res.status}`)
    const blob = await res.blob()
    const objectUrl = URL.createObjectURL(blob)
    // Every holder let go while the bytes were on their way.
    if (entry.released) URL.revokeObjectURL(objectUrl)
    else entry.objectUrl = objectUrl
    return { objectUrl, gone: false, error: false }
  } catch {
    return { objectUrl: null, gone: false, error: true }
  }
}

/** One file's bytes as an object URL, for as long as the component shows it
 * (SA-1094: lifted from `InlineMedia` so a gallery cell loads the same way;
 * SA-1122: through the shared hold, so the frame and the row fetch once). */
export function useMediaBlob(mediaUrl: string): MediaBlobState {
  const [state, setState] = useState<MediaBlobState>(LOADING)

  useEffect(() => {
    let cancelled = false
    setState(LOADING)
    const lease = acquireMediaBlob(mediaUrl)
    void lease.result.then((next) => {
      if (!cancelled) setState(next)
    })
    return () => {
      cancelled = true
      lease.release()
    }
  }, [mediaUrl])

  return state
}
